/**
 * FollowupPanel — #15 Auto-follow-up agent instellingen + handmatige run.
 * Toggle: onbeantwoorde sent-berichten krijgen na X dagen automatisch een
 * opvolg-concept (andere invalshoek) klaargezet in de Upcoming-inbox.
 * Data: GET/PATCH /followup/settings · POST /followup/run
 */
import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import i18n from "i18next";
import { RotateCcw, ChevronDown, ChevronUp, Loader2, Play } from "lucide-react";
import { Switch } from "../ui/switch";

export default function FollowupPanel() {
    const [cfg, setCfg] = useState(null);
    const [open, setOpen] = useState(false);
    const [running, setRunning] = useState(false);
    const [lastRun, setLastRun] = useState(null);

    useEffect(() => {
        api.get("/followup/settings").then((r) => setCfg(r.data)).catch(() => {});
    }, []);

    if (!cfg) return null;

    const patch = async (upd) => {
        try {
            const r = await api.patch("/followup/settings", upd);
            setCfg(r.data);
        } catch (e) { toast.error(e?.response?.data?.detail || "Opslaan mislukt"); }
    };

    const runNow = async () => {
        setRunning(true);
        try {
            const r = await api.post("/followup/run");
            setLastRun(r.data);
            const msg = r.data.created > 0
                ? i18n.t("followup.run_created", { n: r.data.created })
                : i18n.t("followup.run_none");
            toast.success(msg);
            api.get("/followup/settings").then((res) => setCfg(res.data)).catch(() => {});
        } catch (e) { toast.error(e?.response?.data?.detail || "Sweep mislukt"); }
        finally { setRunning(false); }
    };

    return (
        <div className="mb-4 rounded-xl border border-white/10 bg-[var(--jr-surface)]" data-testid="followup-panel">
            <button onClick={() => setOpen(!open)} data-testid="followup-toggle-panel"
                className="w-full flex items-center gap-3 px-4 py-3 text-left">
                <RotateCcw size={15} className="text-[#a78bfa]" />
                <span className="text-sm font-medium text-white">{i18n.t("followup.title")}</span>
                <span className={`text-[10px] px-2 py-0.5 rounded-full border font-mono uppercase tracking-wide ${cfg.enabled
                    ? "text-[#22c55e] border-[#22c55e]/40" : "text-[var(--jr-text-3)] border-white/15"}`}>
                    {cfg.enabled ? i18n.t("followup.on") : i18n.t("followup.off")}
                </span>
                {cfg.enabled && (
                    <span className="text-[10px] text-[var(--jr-text-3)]">
                        {i18n.t("followup.summary", { days: cfg.days, max: cfg.max_followups })}
                    </span>
                )}
                <span className="ml-auto text-[var(--jr-text-3)]">{open ? <ChevronUp size={14} /> : <ChevronDown size={14} />}</span>
            </button>
            {open && (
                <div className="px-4 pb-4 space-y-3">
                    <p className="text-[11px] text-[var(--jr-text-2)]">{i18n.t("followup.explain")}</p>
                    <div className="flex items-center gap-5 flex-wrap">
                        <label className="flex items-center gap-2 text-xs text-[var(--jr-text-2)]">
                            <Switch checked={cfg.enabled} data-testid="followup-enabled"
                                onCheckedChange={(v) => patch({ enabled: v })} />
                            {i18n.t("followup.enable_lbl")}
                        </label>
                        <label className="flex items-center gap-2 text-xs text-[var(--jr-text-2)]">
                            {i18n.t("followup.days_lbl")}
                            <input type="number" min={1} max={30} value={cfg.days} data-testid="followup-days"
                                onChange={(e) => patch({ days: Math.max(1, Math.min(30, parseInt(e.target.value || "4", 10))) })}
                                className="w-14 bg-[var(--jr-surface-2)] border border-white/10 rounded px-2 py-1 text-sm text-white outline-none focus:border-[#a78bfa]" />
                        </label>
                        <label className="flex items-center gap-2 text-xs text-[var(--jr-text-2)]">
                            {i18n.t("followup.max_lbl")}
                            <input type="number" min={1} max={5} value={cfg.max_followups} data-testid="followup-max"
                                onChange={(e) => patch({ max_followups: Math.max(1, Math.min(5, parseInt(e.target.value || "2", 10))) })}
                                className="w-14 bg-[var(--jr-surface-2)] border border-white/10 rounded px-2 py-1 text-sm text-white outline-none focus:border-[#a78bfa]" />
                        </label>
                        <button onClick={runNow} disabled={running} data-testid="followup-run-now"
                            className="ml-auto inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border border-[#a78bfa]/50 text-[#a78bfa] hover:bg-[#a78bfa]/10 font-medium disabled:opacity-50">
                            {running ? <Loader2 size={12} className="animate-spin" /> : <Play size={12} />}
                            {i18n.t("followup.run_btn")}
                        </button>
                    </div>
                    <div className="text-[10px] text-[var(--jr-text-3)] font-mono">
                        {i18n.t("followup.today_count", { n: cfg.created_today, cap: cfg.daily_cap })}
                        {lastRun && ` · ${i18n.t("followup.last_result", { created: lastRun.created, skipped: lastRun.skipped })}`}
                    </div>
                </div>
            )}
        </div>
    );
}
